import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import cx from 'classnames';
import { Icon } from '@storable/canaveral-components';
import useCurrentPage from '../../../hooks/useCurrentPage';
import styles from './NavigationItem.scss';
import StackNavigator from '../StackNavigator';

const itemPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  link: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string,
  }),
  page: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }),
  text: PropTypes.string,
});

const propTypes = {
  inverseBackground: PropTypes.bool,
  item: itemPropTypes.isRequired,
  topLevel: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  expandedGroup: itemPropTypes,
};

const defaultProps = {
  inverseBackground: undefined,
  topLevel: undefined,
  expandedGroup: undefined,
};

const NavItem = ({ item, topLevel, onClick, expandedGroup, inverseBackground }) => {
  const {
    text,
    page,
    link,
    id,
    navigationItems,
  } = item;
  const { primaryColorBorderHoverClass, primaryColorBackgroundClass } = useCurrentPage();

  const displayText = text || page?.title;

  const href = link?.href || page?.path;
  const target = link?.target || '_self';

  const expanded = expandedGroup === id;

  const className = cx(
    styles.item, {
      [primaryColorBorderHoverClass]: topLevel && !inverseBackground,
      [primaryColorBackgroundClass]: topLevel && inverseBackground,
      [styles.inverseBackground]: topLevel && inverseBackground,
      [styles.topLevel]: topLevel,
      [styles.subItem]: !topLevel,
    },
  );

  if (href) {
    return (
      <a href={href} target={target} className={className}>
        {displayText}
      </a>
    );
  }

  return (
    <span>
      <button className={cx(className, styles.group)} onClick={() => onClick(id)} type="button">
        {displayText}
        <Icon
          id={topLevel ? 'expand' : 'select-arrow'}
          label="group"
          className={cx(styles.arrow, { [styles.subArrow]: !topLevel })}
        />
      </button>
      <StackNavigator
        visible={expanded}
        transition={'right'}
        items={navigationItems}
        isDesktop
      />
    </span>
  );
};

NavItem.propTypes = propTypes;
NavItem.defaultProps = defaultProps;

NavItem.fragments = (() => {
  const page = gql`
    fragment NavigationItemPageFields on Page {
      path
      title
    }
  `;

  const navigationItemFields = gql`
    fragment NavigationItemFields on NavigationItem {
      id
      text
      link {
        href
        target
      }
      page {
        id
        ...NavigationItemPageFields
      }
    }

    ${page}
  `;

  return {
    navigationItem: gql`
      fragment NavigationItems on NavigationItem {
        ...NavigationItemFields
        navigationItems {
          ...NavigationItemFields
          navigationItems {
            ...NavigationItemFields
            navigationItems {
              ...NavigationItemFields
            }
          }
        }
      }

      ${navigationItemFields}
    `,
    page,
  };
})();

export default NavItem;
